import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Body5, Body7, Container } from '@ui';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { createTimeInstance } from '@utils/time';
import { showSuccessBanner } from '@utils/application';
import {
  createScoreBackfill,
  marketCapVersionParams,
  previewScoreBackfill,
  SCOPE_OPTIONS,
  stampAtScoringTimeParams,
  UNIVERSE_KINDS,
} from '@services/scoreBackfills';
import { SpecSelect } from './SpecSelect';
import { UniversePicker } from './UniversePicker';
import { DateWindow } from './DateWindow';
import { BackfillPreviewPanel } from './BackfillPreviewPanel';
import { SelectableCard } from './SelectableCard';
import {
  formatCount,
  formatDate,
  formatUsd,
  formatWindow,
  formatScope,
  formatUniverseKind,
  formatIndex,
  formatMarketCapVersion,
  offeredMarketCapVersions,
  specRequiresStamp,
  MARKET_CAP_COMPARE_NOTE,
  STAMPED_AT_SCORING_TIME_LABEL,
} from './format';

const FieldRow = styled.div`
  margin-bottom: 28px;
`;

const ScopeOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ScopeOptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ErrorText = styled(Body5)`
  color: ${({ theme }) => theme.themeColors.error};
  margin-top: 4px;
  margin-bottom: 8px;
`;

const FeedbackText = styled(Body7)`
  color: ${({ theme }) => theme.themeColors.warning};
  display: block;
  margin-top: 8px;
`;

const SectionDivider = styled.div`
  border-top: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  margin-top: 24px;
  padding-top: 16px;
  margin-bottom: 16px;
`;

const ConfirmPanel = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  padding: 16px;
`;

// Passive information, so lowContrastBorder (the SelectableCard note): the stamp is a property of the
// chosen spec, told once under the picker, not a control.
const NoticeBox = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  padding: 12px;
  margin-top: 12px;
`;

const PREVIEW_DEBOUNCE_MS = 400;
const RUN_HINT_ID = 'score-backfill-run-hint';
const SCOPE_FULL_DESC_ID = 'score-backfill-scope-full-desc';
const SCOPE_LATEST_DESC_ID = 'score-backfill-scope-latest-desc';

const toISO = (date) => (date ? createTimeInstance(date).format('YYYY-MM-DD') : null);

export const ScoreBackfillForm = ({ specs, universeOptions, formState, onFormChange, onJobCreated }) => {
  const dispatch = useDispatch();

  const {
    specId,
    universeKind,
    indexValue,
    tickerTags,
    fromDate,
    toDate,
    scope,
    marketCapVersion,
    windowSeededFromLegacy,
  } = formState;

  const setField = (patch) => onFormChange((prev) => ({ ...prev, ...patch }));

  // A spec that enables a present-day context setting (web/social, fundamentals, estimates, holdings,
  // price returns, FYE, abbreviations) can only be backfilled stamped at scoring time — fapi's
  // force_knowable_at_to_scoring_time: each company's latest occasion knowable between the start date
  // and the moment the run is requested, every score stamped when it is scored rather than at the
  // occasion. The picker entry says so (backfill_allowed: false), and the mode is DERIVED from it
  // rather than held as a field: it then holds however the spec was chosen (picker, ?spec=, "Start a
  // similar run") with no effect keeping two values in step, and a spec the server does not mark is
  // sent exactly the request it was sent before the flag existed.
  const selectedSpec = (specs || []).find((s) => s.score_spec_id === specId) || null;
  const stamped = specRequiresStamp(selectedSpec);
  // What the run actually has. A stamped run scores each company's latest occasion whatever the Scope
  // cards (not shown then) were last set to; formState.scope is left alone so a switch back to a
  // standard spec restores the analyst's choice.
  const effectiveScope = stamped ? SCOPE_OPTIONS.LATEST_PER_COMPANY : scope;

  const [preview, setPreview] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState(null);
  const [isReestimating, setIsReestimating] = React.useState(false);

  const [createError, setCreateError] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isConfirming, setIsConfirming] = React.useState(false);
  const [tickerFeedback, setTickerFeedback] = React.useState(null);

  const maxTickers = universeOptions?.max_tickers || 1000;
  const maxWindowYears = universeOptions?.max_window_years || 15;
  const marketCapVersions = offeredMarketCapVersions(universeOptions);
  // A choice of one is not a choice. False everywhere until v2's load lands, at which point the
  // control appears with no frontend deploy.
  const offersMarketCapChoice = marketCapVersions.length > 1;
  const maxDate = createTimeInstance().toDate();
  const minDate = createTimeInstance(maxDate).subtract(maxWindowYears, 'year').toDate();

  const handleAddTicker = (x) => {
    const symbol = String(x.symbol || x).toUpperCase();
    const exists = tickerTags.some((t) => String(t.symbol || t).toUpperCase() === symbol);
    if (exists) {
      setTickerFeedback(`${symbol} is already in the list.`);
      return;
    }
    if (tickerTags.length >= maxTickers) {
      setTickerFeedback(`Maximum of ${formatCount(maxTickers)} tickers reached.`);
      return;
    }
    setTickerFeedback(null);
    setField({ tickerTags: [...tickerTags, x.symbol ? x : { symbol }] });
  };

  // Batch add from bulk paste. Symbols arrive already upper-cased from the picker.
  const handleAddTickers = (symbols) => {
    let added = 0;
    let duplicates = 0;
    let overCap = 0;
    const next = [...tickerTags];
    symbols.forEach((sym) => {
      const symbol = String(sym).toUpperCase();
      const exists = next.some((t) => String(t.symbol || t).toUpperCase() === symbol);
      if (exists) {
        duplicates += 1;
        return;
      }
      if (next.length >= maxTickers) {
        overCap += 1;
        return;
      }
      next.push({ symbol });
      added += 1;
    });
    if (added > 0) setField({ tickerTags: next });
    setTickerFeedback(null);
    return { added, duplicates, overCap };
  };

  const handleRemoveTicker = (tag) => {
    setField({ tickerTags: tickerTags.filter((t) => t !== tag) });
  };

  const buildUniverse = () => {
    if (universeKind === UNIVERSE_KINDS.WHOLE_CORPUS) return { kind: UNIVERSE_KINDS.WHOLE_CORPUS };
    if (universeKind === UNIVERSE_KINDS.INDEX) return { kind: UNIVERSE_KINDS.INDEX, value: indexValue };
    return { kind: UNIVERSE_KINDS.TICKERS, value: tickerTags.map((t) => t.symbol).filter(Boolean) };
  };

  const universeValid = () => {
    if (universeKind === UNIVERSE_KINDS.WHOLE_CORPUS) return true;
    if (universeKind === UNIVERSE_KINDS.INDEX) return !!indexValue;
    const symbols = tickerTags.map((t) => t.symbol).filter(Boolean);
    return symbols.length >= 1 && symbols.length <= maxTickers;
  };

  const universeSummary = () => {
    if (universeKind === UNIVERSE_KINDS.WHOLE_CORPUS) return formatUniverseKind(universeKind);
    if (universeKind === UNIVERSE_KINDS.INDEX)
      return `${formatUniverseKind(universeKind)} — ${formatIndex(indexValue)}`;
    const n = tickerTags.length;
    return `${formatUniverseKind(universeKind)} — ${n} ticker${n === 1 ? '' : 's'}`;
  };

  // Returns a validation message when the form is not yet submittable, else null.
  const validationMessage = () => {
    if (!specId) return 'Select a score spec.';
    if (!universeValid()) {
      if (universeKind === UNIVERSE_KINDS.INDEX) return 'Select an index.';
      return 'Add at least one ticker.';
    }
    if (stamped) {
      // The window ends at the moment the run is requested, so only its start is the analyst's; the
      // end date the form still holds (from a standard run, or a seed) plays no part.
      if (!fromDate) return 'Choose a start date.';
      const from = createTimeInstance(fromDate);
      const today = createTimeInstance(maxDate);
      if (from.isAfter(today, 'day')) return 'The start date must be on or before today.';
      if (today.diff(from, 'year', true) > maxWindowYears) return `The window cannot exceed ${maxWindowYears} years.`;
      return null;
    }
    if (!fromDate || !toDate) return 'Choose a start and end date.';
    const from = createTimeInstance(fromDate);
    const to = createTimeInstance(toDate);
    if (from.isAfter(to)) return 'The start date must be on or before the end date.';
    if (to.diff(from, 'year', true) > maxWindowYears) return `The window cannot exceed ${maxWindowYears} years.`;
    return null;
  };

  const invalidMessage = validationMessage();
  const isFormValid = invalidMessage === null;

  const buildBody = () => ({
    score_spec_id: specId,
    universe: buildUniverse(),
    from: toISO(fromDate),
    // A stamped run has NO `to` and NO `scope` — structurally absent, not null. Its window ends at the
    // moment Rails receives it and its scope is each company's latest occasion; Rails answers 400 to a
    // flagged body that names `to`, and sanitizeJsonRequestBody preserves a null (see the note on
    // marketCapVersionParams), so the keys must not exist. Spread, like the two fragments below, so the
    // standard body is byte-identical to what it was.
    ...(stamped ? {} : { to: toISO(toDate), scope }),
    ...stampAtScoringTimeParams(stamped),
    // TOP-LEVEL, never inside `universe` — nested there the API answers 400. buildUniverse() has no
    // access to this value by construction, so the mistake is not available to make here. Spread, so
    // an unchosen version leaves the key ABSENT ("unchanged, v1") rather than present-and-null, which
    // is an unknown value and also a 400. No list gate needed: the page clamps formState's value to
    // the offered list, so it is always either null or something the server advertised.
    ...marketCapVersionParams(marketCapVersion),
  });

  // Debounced live preview. A stale in-flight response cannot overwrite a newer one.
  // Prior stats stay visible while re-estimating (#14) — never blank on every keystroke.
  React.useEffect(() => {
    if (!isFormValid) {
      setPreview(null);
      setIsReestimating(false);
      setPreviewLoading(false);
      setPreviewError(null);
      return undefined;
    }

    const status = { isCanceled: false };
    setPreviewError(null);
    if (preview === null) {
      setPreviewLoading(true);
    } else {
      setIsReestimating(true);
    }

    const timer = setTimeout(async () => {
      const { preview: result, error } = await previewScoreBackfill(buildBody());
      if (status.isCanceled) return;
      setPreviewLoading(false);
      setIsReestimating(false);
      if (result) {
        setPreview(result);
        setPreviewError(null);
      } else {
        setPreviewError(error);
      }
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      status.isCanceled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // marketCapVersion is a dep on both effects: the band changes which companies are in scope, so a
    // flip must re-estimate, and an open confirm panel must not launch one band behind another's
    // count. universeOptions is deliberately NOT a dep — the only way the offered list reaches the
    // body is by changing marketCapVersion, which is already here. stamped is a function of specId
    // and the spec list; listed too, so a list refresh that changes a spec's mark re-estimates with
    // the right body and closes a confirm panel built on the wrong one.
  }, [specId, stamped, universeKind, indexValue, tickerTags, fromDate, toDate, scope, marketCapVersion]);

  // Any form change makes an open confirm panel stale — close it (#9).
  React.useEffect(() => {
    setIsConfirming(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // marketCapVersion is a dep on both effects: the band changes which companies are in scope, so a
    // flip must re-estimate, and an open confirm panel must not launch one band behind another's
    // count. universeOptions is deliberately NOT a dep — the only way the offered list reaches the
    // body is by changing marketCapVersion, which is already here. stamped is a function of specId
    // and the spec list; listed too, so a list refresh that changes a spec's mark re-estimates with
    // the right body and closes a confirm panel built on the wrong one.
  }, [specId, stamped, universeKind, indexValue, tickerTags, fromDate, toDate, scope, marketCapVersion]);

  const canRun =
    isFormValid &&
    !!preview &&
    !preview.over_ceiling &&
    preview.candidate_count > 0 &&
    !previewLoading &&
    !isReestimating &&
    !previewError &&
    !isSubmitting;

  const runBlockerMessage = () => {
    if (previewLoading) return 'Waiting for the cost estimate…';
    if (isReestimating) return 'Updating the cost estimate…';
    if (previewError) return 'Can’t estimate cost — see the error above.';
    if (preview && preview.over_ceiling) return 'Estimated cost is over the cap — narrow the run.';
    if (preview && preview.candidate_count === 0)
      return 'Nothing matches this spec, universe, and window — widen the window or universe.';
    return invalidMessage;
  };

  // First click opens the confirm panel; the actual POST happens in handleConfirm (#9).
  // Not a native form submit — the shared button components render <button> without a `type`,
  // so any of them inside a <form> would submit it; we drive the flow with explicit onClick.
  const handleStartClick = () => {
    if (!canRun) return;
    setCreateError(null);
    setIsConfirming(true);
  };

  const handleConfirm = async () => {
    setCreateError(null);
    setIsSubmitting(true);
    const { job, error } = await createScoreBackfill(buildBody());
    setIsSubmitting(false);

    if (job) {
      showSuccessBanner('Backfill started', null, dispatch);
      setIsConfirming(false);
      onJobCreated(job);
    } else {
      setCreateError(error);
    }
  };

  const tickerCount = universeKind === UNIVERSE_KINDS.TICKERS ? tickerTags.length : undefined;

  // The spec comes first and alone. Everything after it — universe, window, scope, the estimate —
  // describes a run OF that spec, so it appears once there is one and goes away if the spec is
  // cleared. The hidden fields keep their values: they are owned by the page, not by this render.
  const hasSpec = !!specId;

  return (
    <div>
      <FieldRow>
        <Container bottom={4}>
          <Body5 bold>Score spec</Body5>
        </Container>
        <Container bottom={12}>
          <Body7 isLowContrast>The scoring model to run against each matching document.</Body7>
        </Container>
        <SpecSelect specs={specs} value={specId} onChange={(id) => setField({ specId: id })} />
        {!hasSpec && (
          <Container top={8}>
            <Body7 isLowContrast>Choose a spec to set up the universe, window, and scope for the run.</Body7>
          </Container>
        )}
        {hasSpec && stamped && (
          <NoticeBox>
            <Body5 bold>{STAMPED_AT_SCORING_TIME_LABEL}</Body5>
            <Container top={4}>
              <Body5>
                This spec uses present-day context, so its backfill is stamped at scoring time. It scores each company’s
                latest occasion knowable between the start date and the moment you start the run, and stamps each score
                when it is scored rather than at the occasion.
              </Body5>
            </Container>
            <Container top={4}>
              <Body7 isLowContrast>
                Turn on live firing for the spec first, and publish it only over windows that start at or after this
                run.
              </Body7>
            </Container>
          </NoticeBox>
        )}
      </FieldRow>

      {hasSpec && (
        <>
          <FieldRow>
            <Container bottom={12}>
              <Body5 bold>Universe</Body5>
            </Container>
            <UniversePicker
              kind={universeKind}
              onKindChange={(k) => setField({ universeKind: k })}
              indexValue={indexValue}
              onIndexChange={(v) => setField({ indexValue: v })}
              tickerTags={tickerTags}
              onAddTicker={handleAddTicker}
              onAddTickers={handleAddTickers}
              onRemoveTicker={handleRemoveTicker}
              universeOptions={universeOptions}
            />
            {tickerFeedback && universeKind === UNIVERSE_KINDS.TICKERS && <FeedbackText>{tickerFeedback}</FeedbackText>}
          </FieldRow>

          {/* Hidden while the server offers one version, which is every render until v2's table is
              loaded: a radio group of one poses a question with no second answer, and a disabled
              control would read as "you are not allowed", which is false. Provenance belongs on the
              JOB (History / Progress), not on a form that offers one possibility.

              Rendered here rather than inside UniversePicker: that component's entire prop surface is
              the `universe` object and buildUniverse() is what serializes it, so keeping the control
              out means the function that builds `universe` has no access to the one value that must
              never go in `universe`. Adjacency does the grouping. */}
          {offersMarketCapChoice && (
            <FieldRow>
              <Container bottom={4}>
                <Body5 bold>Market cap</Body5>
              </Container>
              <Container bottom={12}>
                <Body7 isLowContrast>Which market-cap band the universe is drawn from.</Body7>
              </Container>
              <ScopeOptions role="radiogroup" aria-label="Market cap">
                {marketCapVersions.map((version) => (
                  <SelectableCard key={version} selected={marketCapVersion === version}>
                    <input
                      type="radio"
                      name="marketCapVersion"
                      checked={marketCapVersion === version}
                      onChange={() => setField({ marketCapVersion: version })}
                    />
                    <ScopeOptionText>
                      <Body5 bold>{formatMarketCapVersion(version)}</Body5>
                    </ScopeOptionText>
                  </SelectableCard>
                ))}
              </ScopeOptions>
              <Container top={8}>
                <Body7 isLowContrast>{MARKET_CAP_COMPARE_NOTE}</Body7>
              </Container>
            </FieldRow>
          )}

          <FieldRow>
            <Container bottom={4}>
              <Body5 bold>Knowable window</Body5>
            </Container>
            <Container bottom={12}>
              {/* The window bounds each occasion's utc_knowable_at — its trigger time, as UTC days — for
              every spec kind (ai_copilot#592). The same clock as the feed's knowable window, and the
              same name, so an analyst who reads a feed back from a run sees one vocabulary. */}
              {stamped ? (
                <Body7 isLowContrast>
                  Start date only — the window ends the moment the run is requested. Each company’s latest occasion
                  knowable in that range is scored once, in UTC days, on the same clock as the feed’s knowable window.
                </Body7>
              ) : (
                <Body7 isLowContrast>
                  Bounds occasions by when they became knowable, in UTC days. Each occasion whose trigger lands in this
                  range is scored once. Same clock as the feed’s knowable window.
                </Body7>
              )}
            </Container>
            <DateWindow
              fromDate={fromDate}
              toDate={toDate}
              onFromChange={(d) => setField({ fromDate: d, windowSeededFromLegacy: false })}
              onToChange={(d) => setField({ toDate: d, windowSeededFromLegacy: false })}
              maxDate={maxDate}
              minDate={minDate}
              maxWindowYears={maxWindowYears}
              legend="Knowable window"
              toFixed={stamped}
            />
            {/* Set by "Start a similar run" off a job created before the switch (see isLegacyWindow in
            ./format). Its dates bounded document dates; carried over unchanged they now bound a
            different column, so the run can select a different set. Any edit to either date clears
            it — the dates are then the analyst's, not the old run's. */}
            {windowSeededFromLegacy && (
              <FeedbackText>
                These dates came from a run whose window bounded document dates. This run bounds when occasions became
                knowable, so the same dates may select different occasions.
              </FeedbackText>
            )}
          </FieldRow>

          <FieldRow>
            <Container bottom={12}>
              <Body5 bold>Scope</Body5>
            </Container>
            {/* A stamped run scores each company's latest occasion — not a choice. A static line, not a
                disabled radio group: a disabled control reads as "you are not allowed", which is false
                (the market-cap picker above makes the same call). */}
            {stamped ? (
              <Body7 isLowContrast>
                Latest per company — a stamped run scores each company’s latest occasion in the window, so there is no
                choice to make here.
              </Body7>
            ) : (
              <ScopeOptions role="radiogroup" aria-label="Scope">
                <SelectableCard selected={scope === SCOPE_OPTIONS.FULL_HISTORY}>
                  <input
                    type="radio"
                    name="scope"
                    checked={scope === SCOPE_OPTIONS.FULL_HISTORY}
                    onChange={() => setField({ scope: SCOPE_OPTIONS.FULL_HISTORY })}
                    aria-describedby={SCOPE_FULL_DESC_ID}
                  />
                  <ScopeOptionText>
                    <Body5 bold>Full history</Body5>
                    <Body7 id={SCOPE_FULL_DESC_ID} isLowContrast>
                      Score every matching document — a company that reported N times scores N times. Best for building
                      a time-series panel.
                    </Body7>
                  </ScopeOptionText>
                </SelectableCard>
                <SelectableCard selected={scope === SCOPE_OPTIONS.LATEST_PER_COMPANY}>
                  <input
                    type="radio"
                    name="scope"
                    checked={scope === SCOPE_OPTIONS.LATEST_PER_COMPANY}
                    onChange={() => setField({ scope: SCOPE_OPTIONS.LATEST_PER_COMPANY })}
                    aria-describedby={SCOPE_LATEST_DESC_ID}
                  />
                  <ScopeOptionText>
                    <Body5 bold>Latest per company</Body5>
                    <Body7 id={SCOPE_LATEST_DESC_ID} isLowContrast>
                      One score per company from its most recent matching document — a snapshot as of now, far fewer
                      candidates.
                    </Body7>
                  </ScopeOptionText>
                </SelectableCard>
              </ScopeOptions>
            )}
          </FieldRow>

          <SectionDivider>
            <Body5 bold>Review &amp; launch</Body5>
          </SectionDivider>

          <BackfillPreviewPanel
            preview={preview}
            isLoading={previewLoading}
            isReestimating={isReestimating}
            error={previewError}
            scope={effectiveScope}
            tickerCount={tickerCount}
          />

          {isConfirming ? (
            <Container top={12}>
              <ConfirmPanel>
                <Container bottom={12}>
                  <Body5 bold>Confirm backfill</Body5>
                </Container>
                <Container row spaceBetween bottom={6}>
                  <Body7 isLowContrast>Candidates</Body7>
                  <Body5>{formatCount(preview?.candidate_count)}</Body5>
                </Container>
                <Container row spaceBetween bottom={6}>
                  <Body7 isLowContrast>Estimated cost</Body7>
                  <Body5>{formatUsd(preview?.cost_estimate_usd)}</Body5>
                </Container>
                <Container row spaceBetween bottom={6}>
                  <Body7 isLowContrast>Window</Body7>
                  <Body5>
                    {stamped
                      ? `${formatDate(preview?.resolved_from)} → when started`
                      : formatWindow(preview?.resolved_from, preview?.resolved_to)}
                  </Body5>
                </Container>
                <Container row spaceBetween bottom={6}>
                  <Body7 isLowContrast>Universe</Body7>
                  <Body5>{universeSummary()}</Body5>
                </Container>
                {/* Gated by the same boolean as the picker, so the panel can never summarize a
                    field the analyst was never shown. */}
                {offersMarketCapChoice && (
                  <Container row spaceBetween bottom={6}>
                    <Body7 isLowContrast>Market cap</Body7>
                    {/* null formats as v1, which is exactly what omitting the field asks for. */}
                    <Body5>{formatMarketCapVersion(marketCapVersion)}</Body5>
                  </Container>
                )}
                {/* Only when the notice under the picker was shown: never summarise a field the analyst
                    was not shown, the rule the market-cap row above follows. */}
                {stamped && (
                  <Container row spaceBetween bottom={6}>
                    <Body7 isLowContrast>Stamp</Body7>
                    <Body5>At scoring time</Body5>
                  </Container>
                )}
                <Container row spaceBetween bottom={12}>
                  <Body7 isLowContrast>Scope</Body7>
                  <Body5>{formatScope(effectiveScope)}</Body5>
                </Container>
                {createError && <ErrorText>{createError}</ErrorText>}
                <Container row spaceBetween verticallyCenter>
                  <TransparentFlatButton type="button" onClick={() => setIsConfirming(false)}>
                    Back
                  </TransparentFlatButton>
                  <FlatButton type="button" onClick={handleConfirm} disabled={isSubmitting}>
                    {isSubmitting ? 'Starting…' : 'Start backfill'}
                  </FlatButton>
                </Container>
              </ConfirmPanel>
            </Container>
          ) : (
            <Container top={12}>
              {createError && <ErrorText>{createError}</ErrorText>}
              <FlatButton
                onClick={handleStartClick}
                aria-disabled={!canRun}
                aria-describedby={!canRun ? RUN_HINT_ID : undefined}
                fullWidth
              >
                {isSubmitting ? 'Starting…' : 'Start backfill'}
              </FlatButton>
              {!canRun && runBlockerMessage() && (
                <Container top={8}>
                  <Body7 id={RUN_HINT_ID} isLowContrast>
                    {runBlockerMessage()}
                  </Body7>
                </Container>
              )}
            </Container>
          )}
        </>
      )}
    </div>
  );
};
